import {
  type RealtimeEvent,
  realtimeEventSchema,
  type SubscribeResponse,
  WS_EVENTS,
} from "@station/contracts";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { io, type Socket } from "socket.io-client";
import { API_URL } from "./api.js";
import { authStore } from "./auth-store.js";
import { queryKeys } from "./query.js";

export type RealtimeStatus = "idle" | "connecting" | "connected" | "disconnected";

/**
 * Socket.IO bağlantı adresi. API tek alan adında yol önekiyle sunuluyorsa (ör. `https://panel.example/api`) önek `path`'e
 * taşınır: Socket.IO adresteki yolu NAMESPACE sayar ve sunucuda olmayan `/api` ad alanına bağlanmaya çalışırdı.
 */
export function realtimeEndpoint(apiUrl: string, base: string): { url: string; path: string } {
  const parsed = new URL(apiUrl, base);
  return { url: parsed.origin, path: `${parsed.pathname.replace(/\/+$/, "")}/ws` };
}

/**
 * Gelen olay → geçersiz kılınacak sorgular. Olay yükü tüm kiracı oturumlarına gittiği için hassas veri taşımaz;
 * istemci ayrıntıyı yetki denetimli API'den yeniden çeker. Yeni modüller buraya bir satır ekler.
 */
const INVALIDATIONS: Array<{ match: RegExp; keys: (event: RealtimeEvent) => QueryKey[] }> = [
  {
    match: /^org\.station\./,
    keys: (event) => [queryKeys.stations(), queryKeys.station(event.aggregateId), queryKeys.me],
  },
  { match: /^org\.company\./, keys: () => [queryKeys.companies, queryKeys.me] },
  /**
   * Onay motoru (Prompt 11): talep açılınca / karar verilince / yükseltilince pano rozeti, talep listeleri ve
   * TALEBİ AÇAN belgeler (gider, ödeme partisi, fiyat talebi) birlikte tazelenir — belge durumu karara bağlıdır.
   */
  {
    match: /^approvals\.request\./,
    keys: (event) => [
      queryKeys.approvals(),
      queryKeys.approvalRequest(event.aggregateId),
      queryKeys.expenses(),
      queryKeys.expenseDetail(),
      queryKeys.payments(),
      queryKeys.paymentBatch(),
      queryKeys.priceChangeRequests(),
      queryKeys.cashFlow(),
      // Market fire / sayım farkı onay motorundan geçer (Prompt 16): karar belgenin durumunu ve stoğu değiştirir.
      queryKeys.market(),
    ],
  },
  /**
   * Gider ve ödeme olayları nakit akış projeksiyonunu ve bütçe raporunu da DEĞİŞTİRİR (gider gerçekleşene,
   * ödeme kalemi projeksiyona girer): ilgili modüllerin önbelleği birlikte tazelenir.
   */
  {
    match: /^expenses\./,
    keys: () => [queryKeys.expenses(), queryKeys.expenseDetail(), queryKeys.cashFlow(), queryKeys.budgets()],
  },
  {
    match: /^payments\.batch\./,
    keys: () => [queryKeys.payments(), queryKeys.paymentBatch(), queryKeys.cashFlow()],
  },
  /** ALARM: öngörülen nakit açığı — özet kartları ve projeksiyon yeniden çekilir. */
  { match: /^cashflow\./, keys: () => [queryKeys.cashFlow()] },
  /** ALARM: bütçe sapması — rapor ve bütçe listesi tazelenir. */
  { match: /^budget\./, keys: () => [queryKeys.budgets(), queryKeys.budgetDetail("budget")] },
  /**
   * İstasyon ajanı (Prompt 14): kayıt, iptal, çevrimiçi / çevrimdışı geçişi, saat sapması, cihaz alarmı ve
   * komut olayları. Olay yükü ŞİRKET kimliği taşısa da özet ve liste anahtarları sorgu parametrelerini de
   * içerdiği için tek tek hedeflenemez → modülün tüm öneki (`["agents"]`) geçersiz kılınır.
   * Heartbeat HER SEFERİNDE olay üretmez; ekranlar ayrıca 30 sn'de bir `refetchInterval` ile tazelenir.
   */
  { match: /^agents\./, keys: () => [queryKeys.agents()] },
  /**
   * Yerel kasa (Prompt 15): ajan senkronundan düşen satış / iade, iptal, ödeme düzeltmesi, geç düşen satış ve
   * çevrimdışı veresiye limit aşımı. Satış listesi ve rapor anahtarları sorgu parametrelerini de içerdiği için
   * tek tek hedeflenemez → modülün tüm öneki (`["pos"]`) geçersiz kılınır.
   *
   * VARDİYA da tazelenir: kasiyer hesabının BEKLENEN tutarı artık kasa satışlarından gelir (ADR 0015), yani
   * bir kasa fişi vardiya hesabını, gün sonu panosunu ve cari riskini de değiştirir.
   */
  {
    match: /^pos\./,
    keys: () => [
      queryKeys.pos(),
      queryKeys.shifts(),
      queryKeys.counterparties(),
      queryKeys.market(),
      // Prompt 17: kasa satışı müşteri 360'ını (harcama, ziyaret) ve filo kullanım defterini (worker yazar, ayrı olay
      // üretmez) değiştirir — kalan limit, ekstre ve portal özeti birlikte tazelenir.
      queryKeys.customers(),
      queryKeys.fleet(),
      queryKeys.fleetPortal(),
      // Sadakat defterini de worker yazar: her satış kazanım olayı üretmez (kupon / hak kullanımı ayrı olay taşımaz).
      queryKeys.loyalty(),
    ],
  },
  /**
   * Market (Prompt 16): transfer gönderimi / kabulü, fire kaydı / reddi, sayım gönderimi / onayı, gün kapanışı,
   * negatif stok ve açılış stoğu; aşama B: sipariş durumu (`market.purchase_order.*`), mal kabul kaydı / iptali
   * (`market.goods_receipt.*`), fatura eşleşmesi / kesin maliyet / geri alma (`market.invoice_match.*`), satış belgesi
   * (`market.license.*`) ve SKT (`market.lot.*`) uyarıları. Liste anahtarları sorgu parametrelerini de içerdiği için
   * modülün tüm öneki (`["market"]`) geçersiz kılınır — tek bir mal kabul stoğu, hareketleri, partileri, siparişin
   * ilerlemesini, eşleştirme adaylarını ve fiyat önerisini birlikte değiştirir. Kasa satışı stoğu WORKER'da düşer ve
   * ayrı olay üretmez: yukarıdaki `pos.` kuralı market önbelleğini de tazeler (ekranlar pencere odağında da çeker).
   */
  { match: /^market\./, keys: () => [queryKeys.market()] },
  /**
   * Müşteri ve KVKK (Prompt 17): profil açılışı / anonimleştirme / sürücü bağı (`customers.*`), rıza ve ileti izni
   * kaydı, başvuru durumu ve süre uyarıları (`privacy.*`), pazarlama gönderimi (`marketing.*`). Rıza durumu müşteri
   * 360'ında, anonimleştirme başvuru ekranında göründüğü için iki modül BİRLİKTE tazelenir. Olay yükü kişisel veri
   * taşımaz; ekran ayrıntıyı (maskeleme kurallarıyla) API'den yeniden çeker.
   */
  {
    match: /^(customers|privacy|marketing)\./,
    keys: () => [queryKeys.customers(), queryKeys.privacy()],
  },
  /**
   * Filo (Prompt 17): kural ihlali alarmı, dönem faturası kesimi / iptali, araç talebi açılışı / kararı. Talep kararı
   * araç listesini, fatura kesimi ekstreyi ve portalın fatura listesini değiştirir → personel ve portal önbelleği birlikte.
   */
  { match: /^fleet\./, keys: () => [queryKeys.fleet(), queryKeys.fleetPortal()] },
  /**
   * Sadakat (Prompt 17): puan kazanımı / harcaması / eksi bakiye / süre sonu (+ yaklaşan), kötüye kullanım şüphesi (inceleme
   * kuyruğu), seviye değişimi, kupon kullanımı / çift kullanım ve abonelik açılışı / aşımı. Liste ve rapor anahtarları sorgu
   * parametrelerini taşıdığı için modülün tüm öneki geçersiz kılınır; puan bakiyesi ve seviye müşteri 360'ında da
   * göründüğü için müşteri önbelleği birlikte tazelenir. Olay yükü kişisel veri taşımaz.
   */
  { match: /^loyalty\./, keys: () => [queryKeys.loyalty(), queryKeys.customers()] },
  /**
   * Yıkama (Prompt 18): ünite durumu / arıza, makine çalışması, başlatma sonucu, kredi eksi bakiyesi, iş emri durumu / hazır
   * SMS'i ve randevu olayları İSTASYON odasına gelir (yıkama ekranları seçili istasyonun odasına abone olur). Liste
   * anahtarları sorgu parametrelerini taşıdığı için modülün tüm öneki geçersiz kılınır. Çalışmanın kimyasal tüketimini
   * worker stok defterine yazar (ayrı olay yok) → market önbelleği de tazelenir.
   */
  { match: /^wash\./, keys: () => [queryKeys.wash(), queryKeys.market()] },
  /**
   * Şarj (Prompt 19): ünite bağlantısı / açılışı / arızası, konnektör durumu, oturum başlangıcı / ölçümü (sınırlı sıklık) /
   * tamamlanması / ödemesi, komut sonucu, yük dengeleme ve şarj ağı içe aktarımı. Çoğu İSTASYON odasına gelir (şarj ekranları
   * seçili istasyonun odasına abone olur); kopma, arıza, tamamlanma ve içe aktarım kiracı odasına da düşer. Liste anahtarları
   * sorgu parametrelerini taşıdığı için modülün tüm öneki geçersiz kılınır. Oturum TAMAMLANINCA çalışma zamanı aynı
   * transaction'da filo kullanım defterine (TL limiti, ekstre) ve abonelik hakkına yazar — ayrı olay yok → filo ve sadakat
   * önbelleği de tazelenir.
   */
  {
    match: /^ev\./,
    keys: (event) =>
      event.type === "ev.session.completed"
        ? [queryKeys.ev(), queryKeys.fleet(), queryKeys.loyalty()]
        : [queryKeys.ev()],
  },
  /**
   * Alarmlar (Prompt 20): `alarms.alarm.raised / updated / resolved / escalated` kiracı + istasyon odalarına gelir (kişisel
   * veri yok). Liste anahtarı süzgeçleri taşıdığı için modülün tüm öneki geçersiz kılınır: liste, ana sayfa kartının
   * sayıları ve açık ayrıntının zaman çizelgesi birlikte tazelenir.
   */
  // Prompt 22: istisna odaklı ana sayfanın "sorunlar" bölümü de açık alarmları gösterir.
  { match: /^alarms\./, keys: () => [queryKeys.alarms(), queryKeys.reports("home")] },
  /**
   * Bakım ve uyum (Prompt 23): iş emri açılışı / durum değişikliği (kiracı + istasyon odası) ve uyum belgesi değişikliği
   * (kiracı odası). Ana sayfanın "SLA'sı aşan iş emirleri" ve "süresi dolan belgeler" kartları da tazelenir; takvim iş emri
   * son anlarını da gösterdiği için bakım olayı uyum önbelleğini de geçersiz kılar.
   */
  {
    match: /^maintenance\./,
    keys: () => [queryKeys.maintenance(), queryKeys.compliance("calendar"), queryKeys.reports("home")],
  },
  { match: /^compliance\./, keys: () => [queryKeys.compliance(), queryKeys.reports("home")] },
  /**
   * Bildirimler (Prompt 20): `notifications.notification.created` yalnızca hedef kullanıcının odasına gelir (sunucu
   * kullanıcıyı kendi odasına bağlanırken katar) → zil sayısı, bildirim kutusu ve teslim günlüğü tazelenir.
   */
  { match: /^notifications\./, keys: () => [queryKeys.notifications()] },
  /**
   * Platform ilişkisi (Prompt 24): kiracı durumu (salt okunur / askı), abonelik, kesilen fatura ve destek erişimi olayları
   * kiracı odasına gelir → aboneliğim / destek erişimi önbelleği; durum ya da destek erişimi değişince `/me` de tazelenir
   * (yazma izni ve kabuk bantlarının bildirimleri `/me`'den gelir).
   */
  {
    match: /^platform\./,
    keys: (event) =>
      event.type === "platform.tenant.status_changed" || event.type === "platform.support_access.changed"
        ? [queryKeys.account(), queryKeys.me]
        : [queryKeys.account()],
  },
];

export function queriesToInvalidate(event: RealtimeEvent): QueryKey[] {
  return INVALIDATIONS.filter((rule) => rule.match.test(event.type)).flatMap((rule) => rule.keys(event));
}

/**
 * WebSocket istemcisi: bağlantı yönetimi, yeniden bağlanma, oda aboneliği (Prompt 04).
 *  - Her (yeniden) bağlanmada GÜNCEL erişim token'ı kullanılır (auth işlevi).
 *  - Kopup yeniden bağlanınca odalara yeniden abone olunur ve kaçmış olabilecek olaylara karşı sorgular tazelenir.
 */
export class RealtimeClient {
  private socket: Socket | null = null;
  private readonly rooms = new Set<string>();
  private readonly statusListeners = new Set<(status: RealtimeStatus) => void>();
  private readonly eventListeners = new Set<(event: RealtimeEvent) => void>();
  private currentStatus: RealtimeStatus = "idle";
  private hasConnectedBefore = false;

  constructor(private readonly queryClient: QueryClient) {}

  get status(): RealtimeStatus {
    return this.currentStatus;
  }

  connect(): void {
    if (this.socket) return;
    this.setStatus("connecting");
    const endpoint = realtimeEndpoint(API_URL, window.location.origin);
    const socket = io(endpoint.url, {
      path: endpoint.path,
      transports: ["websocket", "polling"],
      auth: (callback) => callback({ token: authStore.getAccessToken() }),
      reconnectionDelay: 1000,
      reconnectionDelayMax: 15_000,
      randomizationFactor: 0.5,
    });
    socket.on("connect", () => {
      this.setStatus("connected");
      for (const room of this.rooms) void this.emitSubscribe(room);
      if (this.hasConnectedBefore) void this.queryClient.invalidateQueries();
      this.hasConnectedBefore = true;
    });
    socket.on("disconnect", () => this.setStatus("disconnected"));
    socket.on("connect_error", () => this.setStatus("disconnected"));
    socket.on(WS_EVENTS.domainEvent, (payload: unknown) => {
      const parsed = realtimeEventSchema.safeParse(payload);
      if (!parsed.success) return;
      for (const key of queriesToInvalidate(parsed.data))
        void this.queryClient.invalidateQueries({ queryKey: key });
      this.eventListeners.forEach((listener) => listener(parsed.data));
    });
    this.socket = socket;
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.rooms.clear();
    this.hasConnectedBefore = false;
    this.setStatus("idle");
  }

  /** Odaya abone olur; aboneliği bırakan işlevi döndürür. Kiracı ve kullanıcı odalarına sunucu kendiliğinden katar. */
  subscribe(room: string): () => void {
    this.rooms.add(room);
    if (this.socket?.connected) void this.emitSubscribe(room);
    return () => {
      this.rooms.delete(room);
      this.socket?.emit(WS_EVENTS.unsubscribe, { room });
    };
  }

  onEvent(listener: (event: RealtimeEvent) => void): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  onStatus(listener: (status: RealtimeStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private async emitSubscribe(room: string): Promise<void> {
    const response = (await this.socket
      ?.timeout(5000)
      .emitWithAck(WS_EVENTS.subscribe, { room })
      .catch(() => null)) as SubscribeResponse | null;
    // Yetki yoksa oda listeden çıkarılır: her yeniden bağlanmada boşuna denenmez.
    if (response && !response.ok) this.rooms.delete(room);
  }

  private setStatus(status: RealtimeStatus): void {
    this.currentStatus = status;
    this.statusListeners.forEach((listener) => listener(status));
  }
}
